import React, { useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Eye,
  Pin,
  FileText,
  AlignEndHorizontal,
  Sparkles,
  ArrowRight,
  Play,
  Pause,
  Volume2,
  Tv,
  Smartphone,
  Code2,
  Gamepad2,
} from "lucide-react";

const TABS = [
  { id: "Display", icon: AlignEndHorizontal },
  { id: "oRTB", icon: Code2 },
  { id: "Video", icon: Play },
  { id: "CTV", icon: Tv },
  { id: "Apps", icon: Smartphone },
];

// Public sample MP4 (~10 MB, 60 s) — Big Buck Bunny is the de-facto free test clip.
const SAMPLE_VIDEO =
  "https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/BigBuckBunny.mp4";

// ───────────────────────────────────────── Display mockups (real imagery)
const displayUnits = [
  {
    size: "300x250",
    label: "Medium Rectangle",
    desc: "Most versatile standard unit",
    image:
      "https://images.unsplash.com/photo-1494976388531-d1058494cdd8?auto=format&fit=crop&w=600&q=80",
    headline: "PREMIUM BMW",
    body: "Engineered excellence.",
    cta: "Discover",
    accent: "from-[#0047FF] to-[#00F0FF]",
    w: 300,
    h: 250,
  },
  {
    size: "728x90",
    label: "Leaderboard",
    desc: "Header / footer placement",
    image:
      "https://images.unsplash.com/photo-1542291026-7eec264c27ff?auto=format&fit=crop&w=900&q=80",
    headline: "AIR MAX · NEW DROP",
    body: "Limited release.",
    cta: "Shop Now",
    accent: "from-[#8A2BE2] to-[#0047FF]",
    w: 728,
    h: 90,
  },
  {
    size: "970x250",
    label: "Billboard",
    desc: "Wide-format hero unit",
    image:
      "https://images.unsplash.com/photo-1505740420928-5e560c06d30e?auto=format&fit=crop&w=1200&q=80",
    headline: "SOUND THAT MOVES",
    body: "Premium audio. Engineered for you.",
    cta: "Explore Collection",
    accent: "from-[#00F0FF] to-[#0047FF]",
    w: 970,
    h: 250,
  },
  {
    size: "320x50",
    label: "Mobile Banner",
    desc: "Mobile-first anchor unit",
    image:
      "https://images.unsplash.com/photo-1607082348824-0a96f2a4b9da?auto=format&fit=crop&w=600&q=80",
    headline: "WELLNESS SUBSCRIBE",
    body: "First box 50% off.",
    cta: "Claim",
    accent: "from-[#0047FF] to-[#8A2BE2]",
    w: 320,
    h: 50,
  },
  {
    size: "300x600",
    label: "Half Page",
    desc: "High-impact sidebar",
    image:
      "https://images.unsplash.com/photo-1483985988355-763728e1935b?auto=format&fit=crop&w=600&q=80",
    headline: "SS26 LOOKBOOK",
    body: "The new collection is here.",
    cta: "View Drop",
    accent: "from-[#00F0FF] to-[#8A2BE2]",
    w: 300,
    h: 600,
  },
  {
    size: "160x600",
    label: "Wide Skyscraper",
    desc: "Vertical sidebar unit",
    image:
      "https://images.unsplash.com/photo-1542751371-adc38448a05e?auto=format&fit=crop&w=400&q=80",
    headline: "PLAY NOW",
    body: "Free to download.",
    cta: "Install",
    accent: "from-[#8A2BE2] to-[#00F0FF]",
    w: 160,
    h: 600,
  },
];

function DisplayMockup({ unit }) {
  const scale = unit.w > 500 ? 0.55 : unit.w > 300 ? 0.7 : 1;
  const isWide = unit.w / unit.h > 3;
  const isSmall = unit.h <= 90;

  return (
    <div className="tile p-6 flex flex-col items-center">
      <div className="self-start mb-1 text-[10px] uppercase tracking-[0.2em] text-[#00F0FF] font-mono">
        {unit.size}
      </div>
      <div className="self-start text-sm font-medium text-white/85 mb-5">
        {unit.label}
      </div>

      {/* Real ad creative */}
      <div
        className="relative rounded-xl overflow-hidden border border-white/15 shadow-[0_8px_30px_rgba(0,0,0,0.4)]"
        style={{
          width: unit.w * scale,
          height: unit.h * scale,
          maxWidth: "100%",
        }}
      >
        {/* Photo */}
        <img
          src={unit.image}
          alt={unit.headline}
          className="absolute inset-0 w-full h-full object-cover"
          loading="lazy"
        />
        {/* Gradient overlay for legibility */}
        <div
          className={`absolute inset-0 bg-gradient-to-br ${unit.accent} mix-blend-multiply opacity-60`}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />

        {/* AD label */}
        <div className="absolute top-1.5 left-1.5 px-1.5 py-0.5 rounded text-[8px] font-bold tracking-wider bg-yellow-400/95 text-black">
          AD
        </div>

        {/* Content */}
        <div
          className={`absolute z-10 ${
            isWide
              ? "inset-y-0 right-3 flex items-center"
              : isSmall
                ? "inset-0 flex items-center justify-between px-2"
                : "bottom-0 left-0 right-0 p-3"
          }`}
        >
          {isSmall ? (
            <>
              <div>
                <div
                  className="font-display font-bold text-white leading-none"
                  style={{ fontSize: 11 }}
                >
                  {unit.headline}
                </div>
                <div
                  className="text-white/85 leading-tight"
                  style={{ fontSize: 9 }}
                >
                  {unit.body}
                </div>
              </div>
              <div
                className="rounded bg-white text-black px-2 py-0.5 font-bold"
                style={{ fontSize: 9 }}
              >
                {unit.cta}
              </div>
            </>
          ) : (
            <div className={isWide ? "text-right" : ""}>
              <div
                className="font-display font-bold text-white leading-tight"
                style={{ fontSize: Math.max(14, 22 * scale) }}
              >
                {unit.headline}
              </div>
              <div
                className="text-white/85 leading-tight mt-1"
                style={{ fontSize: Math.max(10, 13 * scale) }}
              >
                {unit.body}
              </div>
              <div
                className={`inline-block mt-2 rounded-full bg-white text-black font-semibold ${isWide ? "" : ""}`}
                style={{
                  fontSize: Math.max(9, 11 * scale),
                  padding: `${Math.max(3, 4 * scale)}px ${Math.max(8, 12 * scale)}px`,
                }}
              >
                {unit.cta}
              </div>
            </div>
          )}
        </div>
      </div>

      <p className="text-xs text-white/50 mt-5 text-center">{unit.desc}</p>
    </div>
  );
}

function DisplayPane() {
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
      {displayUnits.map((u) => (
        <DisplayMockup key={u.size} unit={u} />
      ))}
    </div>
  );
}

// ───────────────────────────────────────── oRTB pane
function ORTBPane() {
  const sampleRequest = `{
  "id": "8c3d8f4e-9bid-4abc-9f30",
  "imp": [{
    "id": "1",
    "banner": { "w": 300, "h": 250 },
    "bidfloor": 1.20,
    "secure": 1
  }],
  "site": {
    "id": "monetiseup-12",
    "domain": "publisher.com",
    "cat": ["IAB1", "IAB13"]
  },
  "user": { "id": "u9d2f-1c87" },
  "device": { "ua": "Mozilla/5.0", "geo": { "country": "USA" } },
  "tmax": 120,
  "at": 2
}`;

  const sampleResponse = `{
  "id": "8c3d8f4e-9bid-4abc-9f30",
  "seatbid": [{
    "bid": [{
      "id": "bid-7821",
      "impid": "1",
      "price": 4.85,
      "adm": "<ad markup/>",
      "crid": "creative-9921",
      "w": 300,
      "h": 250
    }]
  }],
  "bidid": "0193ac"
}`;

  return (
    <div className="grid lg:grid-cols-[1.2fr_1fr] gap-6">
      <div className="space-y-6">
        <div className="tile p-6">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#00F0FF] font-mono">
                POST /openrtb/bid
              </div>
              <div className="text-sm font-medium mt-0.5">Bid Request</div>
            </div>
            <span className="text-[10px] px-2 py-1 rounded-full bg-emerald-500/15 text-emerald-400 font-mono">
              200 OK
            </span>
          </div>
          <pre className="text-[11px] leading-relaxed font-mono text-white/75 bg-black/30 rounded-xl p-4 overflow-x-auto border border-white/5">
            {sampleRequest}
          </pre>
        </div>

        <div className="tile p-6">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#00F0FF] font-mono">
                ← 200 RESPONSE
              </div>
              <div className="text-sm font-medium mt-0.5">Bid Response</div>
            </div>
            <span className="text-[10px] font-mono text-white/50">~48 ms</span>
          </div>
          <pre className="text-[11px] leading-relaxed font-mono text-white/75 bg-black/30 rounded-xl p-4 overflow-x-auto border border-white/5">
            {sampleResponse}
          </pre>
        </div>
      </div>

      <div className="space-y-6">
        <div className="tile p-7">
          <h3 className="font-display text-xl font-bold mb-4">
            OpenRTB <span className="cyan-text">2.5 / 2.6 compliant</span>
          </h3>
          <ul className="space-y-3">
            {[
              "Server-to-server (S2S) and client-side bidding",
              "First-price + soft-floor auction support",
              "GDPR, CCPA & TCF v2.2 signal pass-through",
              "Sub-100 ms p95 response time globally",
              "DSP whitelist + supply-path optimization",
            ].map((b) => (
              <li
                key={b}
                className="flex items-start gap-3 text-sm text-white/80"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-[#00F0FF] mt-2 shrink-0" />
                {b}
              </li>
            ))}
          </ul>
        </div>

        <div className="tile p-7">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#00F0FF] mb-2 font-mono">
            Live Auction Metrics
          </div>
          <div className="grid grid-cols-2 gap-4 mt-4">
            <Stat label="Bid Requests/s" value="58,200" />
            <Stat label="Win Rate" value="12.4%" />
            <Stat label="Avg. CPM" value="$4.85" />
            <Stat label="p95 Latency" value="48 ms" />
          </div>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="font-display text-xl font-bold cyan-text">{value}</div>
      <div className="text-[10px] uppercase tracking-wider text-white/50 mt-1">
        {label}
      </div>
    </div>
  );
}

// ───────────────────────────────────────── Sample video player
function SampleVideoPlayer({ poster, label = "AD", className = "" }) {
  const ref = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(true);

  const toggle = () => {
    const v = ref.current;
    if (!v) return;
    if (v.paused) {
      v.play().catch(() => {});
      setPlaying(true);
    } else {
      v.pause();
      setPlaying(false);
    }
  };

  return (
    <div
      className={`relative rounded-2xl overflow-hidden border border-white/10 bg-black ${className}`}
    >
      <video
        ref={ref}
        src={SAMPLE_VIDEO}
        poster={poster}
        muted={muted}
        playsInline
        loop
        preload="metadata"
        className="w-full h-full object-cover aspect-video"
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
      />

      {/* Big play overlay when paused */}
      {!playing && (
        <button
          onClick={toggle}
          className="absolute inset-0 grid place-items-center bg-black/30 hover:bg-black/40 transition-colors"
          aria-label="Play video"
        >
          <div className="w-20 h-20 rounded-full bg-white/15 backdrop-blur-md border border-white/30 grid place-items-center hover:scale-110 transition-transform">
            <Play className="w-9 h-9 text-white ml-1" fill="white" />
          </div>
        </button>
      )}

      {/* AD label */}
      <div className="absolute top-3 left-3 px-2.5 py-1 rounded-md bg-yellow-500/95 text-black text-[10px] font-bold tracking-wider">
        {label}
      </div>

      {/* Controls bar */}
      <div className="absolute bottom-0 inset-x-0 p-3 bg-gradient-to-t from-black/70 to-transparent">
        <div className="flex items-center gap-3 text-white">
          <button
            onClick={toggle}
            className="w-8 h-8 rounded-full bg-white/10 backdrop-blur grid place-items-center hover:bg-white/20"
            aria-label={playing ? "Pause" : "Play"}
          >
            {playing ? (
              <Pause className="w-3.5 h-3.5" />
            ) : (
              <Play className="w-3.5 h-3.5" fill="white" />
            )}
          </button>
          <button
            onClick={() => {
              setMuted((m) => !m);
              if (ref.current) ref.current.muted = !muted;
            }}
            className="text-white/80 hover:text-white"
            aria-label="Toggle mute"
          >
            <Volume2 className="w-4 h-4" />
          </button>
          <div className="flex-1 h-1 rounded-full bg-white/15">
            <div className="h-full w-1/3 rounded-full bg-gradient-to-r from-[#0047FF] to-[#00F0FF]" />
          </div>
          <span className="text-xs font-mono text-white/70">VAST 4.x</span>
        </div>
      </div>
    </div>
  );
}

// ───────────────────────────────────────── Video pane
function VideoPane() {
  const formats = [
    {
      id: "preroll",
      name: "Pre-Roll",
      desc: "Linear ad before content begins. Skippable after 5s.",
      duration: "15-30s",
      vast: "VAST 4.x",
    },
    {
      id: "midroll",
      name: "Mid-Roll",
      desc: "Linear ad inserted at natural content breaks.",
      duration: "15s",
      vast: "VAST 4.x",
    },
    {
      id: "outstream",
      name: "Outstream",
      desc: "Auto-pause when out of view, premium viewability.",
      duration: "10-20s",
      vast: "VPAID 2.0",
    },
  ];

  const [active, setActive] = useState("preroll");
  const cur = formats.find((f) => f.id === active);

  return (
    <div className="grid lg:grid-cols-[1.1fr_1fr] gap-6">
      <div className="tile p-5">
        <SampleVideoPlayer
          label={`AD · ${cur.duration}`}
          poster="https://images.unsplash.com/photo-1485846234645-a62644f84728?auto=format&fit=crop&w=1200&q=80"
        />

        <div className="mt-5 flex flex-wrap gap-2">
          {formats.map((f) => (
            <button
              key={f.id}
              onClick={() => setActive(f.id)}
              data-testid={`video-fmt-${f.id}`}
              className={`text-xs px-3 py-1.5 rounded-full transition-all ${
                active === f.id
                  ? "bg-gradient-to-r from-[#0047FF] to-[#00F0FF] text-white"
                  : "border border-white/10 text-white/65 hover:border-white/30"
              }`}
            >
              {f.name}
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-6">
        <div className="tile p-7">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#00F0FF] mb-2">
            Current Format
          </div>
          <h3 className="font-display text-2xl font-bold mb-2">{cur.name}</h3>
          <p className="text-white/65 text-sm mb-5">{cur.desc}</p>
          <div className="grid grid-cols-2 gap-4">
            <Stat label="Duration" value={cur.duration} />
            <Stat label="Standard" value={cur.vast} />
            <Stat label="Avg. CPM" value="$18.20" />
            <Stat label="Completion" value="74%" />
          </div>
        </div>

        <div className="tile p-7">
          <h3 className="font-display text-lg font-bold mb-4">
            Built-in <span className="cyan-text">guardrails</span>
          </h3>
          <ul className="space-y-2.5">
            {[
              "MOAT / IAS viewability tracking",
              "Brand safety + content categorization",
              "Frequency capping per user/per device",
              "Adaptive bitrate streaming (HLS, DASH)",
            ].map((b) => (
              <li
                key={b}
                className="flex items-start gap-3 text-sm text-white/80"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-[#00F0FF] mt-2 shrink-0" />
                {b}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

// ───────────────────────────────────────── CTV pane
function CTVPane() {
  return (
    <div className="grid lg:grid-cols-[1.2fr_1fr] gap-6">
      {/* TV bezel mockup */}
      <div className="tile p-6">
        <div className="relative mx-auto" style={{ maxWidth: 720 }}>
          <div className="relative rounded-[1.75rem] p-2.5 bg-gradient-to-b from-[#0a0a18] to-[#020208] border border-white/10 shadow-[0_30px_80px_rgba(0,0,0,0.5)]">
            <SampleVideoPlayer
              label="AD · 0:30"
              poster="https://images.unsplash.com/photo-1574375927938-d5a98e8ffe85?auto=format&fit=crop&w=1200&q=80"
              className="rounded-2xl"
            />
          </div>
          {/* Stand */}
          <div className="mx-auto h-3 w-32 bg-gradient-to-b from-[#0a0a18] to-[#020208] rounded-b-xl border-x border-b border-white/10 -mt-px" />
          <div className="text-center text-[10px] text-white/40 font-mono mt-3">
            SmartTV · 1920×1080
          </div>
        </div>
      </div>

      <div className="space-y-6">
        <div className="tile p-7">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#00F0FF] mb-2">
            Connected TV
          </div>
          <h3 className="font-display text-2xl font-bold mb-3">
            Big screens. <span className="cyan-text">Bigger impact.</span>
          </h3>
          <p className="text-white/65 text-sm">
            Premium video ad placements across Smart TVs, streaming sticks, and
            OTT apps. Full-screen, non-skippable, and 100% measurable.
          </p>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="tile p-5"><Stat label="Avg. CPM" value="$32.40" /></div>
          <div className="tile p-5"><Stat label="Completion" value="92%" /></div>
          <div className="tile p-5"><Stat label="Viewability" value="98%" /></div>
          <div className="tile p-5"><Stat label="Households" value="48M" /></div>
        </div>

        <div className="tile p-7">
          <h3 className="font-display text-lg font-bold mb-3">
            Supported <span className="cyan-text">platforms</span>
          </h3>
          <div className="flex flex-wrap gap-2">
            {[
              "Roku",
              "Fire TV",
              "Apple TV",
              "Samsung Tizen",
              "LG webOS",
              "Chromecast",
              "Hisense Vidaa",
            ].map((p) => (
              <span
                key={p}
                className="text-xs px-3 py-1.5 rounded-full border border-white/10 text-white/70"
              >
                {p}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ───────────────────────────────────────── Apps pane
function AppsPane() {
  const formats = [
    {
      id: "rewarded",
      icon: Sparkles,
      name: "Rewarded Video",
      desc: "Opt-in video ads in exchange for in-app currency or content.",
      cpm: "$22.50",
      ecpm: "+38%",
    },
    {
      id: "interstitial",
      icon: Sparkles,
      name: "Interstitial",
      desc: "Full-screen ads at natural app transitions.",
      cpm: "$8.40",
      ecpm: "+12%",
    },
    {
      id: "banner",
      icon: AlignEndHorizontal,
      name: "Banner",
      desc: "Persistent 320×50 / 320×100 anchor units.",
      cpm: "$1.85",
      ecpm: "Stable",
    },
    {
      id: "playable",
      icon: Gamepad2,
      name: "Playable",
      desc: "Mini-interactive ad — try-before-install for games.",
      cpm: "$14.30",
      ecpm: "+24%",
    },
  ];

  return (
    <div className="grid lg:grid-cols-[1fr_1.3fr] gap-6">
      {/* Phone mockup with video playing inside */}
      <div className="tile p-6 flex items-center justify-center">
        <div className="relative" style={{ width: 260 }}>
          <div className="relative rounded-[2.2rem] p-2 bg-gradient-to-b from-[#1a1a2e] to-[#020208] border border-white/10 shadow-[0_30px_60px_rgba(0,0,0,0.5)]">
            <div className="relative rounded-[1.8rem] overflow-hidden aspect-[9/19] bg-gradient-to-b from-[#050B26] to-[#0a1142]">
              <div className="absolute top-2 left-1/2 -translate-x-1/2 w-20 h-5 rounded-full bg-black z-20" />

              <div className="absolute top-2 inset-x-0 flex items-center justify-between px-5 text-[9px] text-white/80 z-20">
                <span>9:41</span>
                <span>5G ●●●</span>
              </div>

              {/* App header */}
              <div className="absolute top-9 inset-x-2 p-2.5 rounded-xl bg-white/[0.06] border border-white/10 z-10">
                <div className="h-1.5 w-3/4 rounded-full bg-white/30 mb-1.5" />
                <div className="h-1.5 w-1/2 rounded-full bg-white/15" />
              </div>

              {/* Rewarded video ad */}
              <div className="absolute top-[25%] inset-x-2 z-10">
                <div className="rounded-xl overflow-hidden border border-[#00F0FF]/40 bg-black">
                  <SampleVideoPlayer
                    label="AD"
                    poster="https://images.unsplash.com/photo-1538481199705-c710c4e965fc?auto=format&fit=crop&w=600&q=80"
                  />
                  <div className="p-2 bg-gradient-to-r from-[#0047FF]/40 to-[#8A2BE2]/30">
                    <div className="text-[9px] uppercase tracking-wider text-yellow-300 mb-0.5">
                      Watch · Earn 50 gems
                    </div>
                    <div className="font-display font-bold text-[11px]">
                      Tap to claim reward
                    </div>
                  </div>
                </div>
              </div>

              {/* Content placeholder bottom */}
              <div className="absolute bottom-12 inset-x-2 space-y-1.5 z-10">
                <div className="h-2.5 rounded-md bg-white/[0.06]" />
                <div className="h-2.5 rounded-md bg-white/[0.06] w-3/4" />
              </div>

              {/* Tab bar */}
              <div className="absolute bottom-0 inset-x-0 h-10 bg-black/50 backdrop-blur-md border-t border-white/5 flex items-center justify-around z-20">
                {["⌂", "⊙", "✨", "☰"].map((s, i) => (
                  <span key={i} className="text-white/60 text-[10px]">
                    {s}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="space-y-4">
        {formats.map((f) => (
          <div
            key={f.name}
            data-testid={`apps-fmt-${f.id}`}
            className="tile p-6 flex items-start gap-4"
          >
            <div className="shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-[#0047FF]/30 to-[#00F0FF]/20 border border-white/10 grid place-items-center">
              <f.icon className="w-5 h-5 text-[#00F0FF]" />
            </div>
            <div className="flex-1">
              <div className="flex items-center justify-between gap-3 mb-1">
                <h3 className="font-display text-lg font-bold">{f.name}</h3>
                <span className="text-xs font-mono text-emerald-400">
                  {f.ecpm}
                </span>
              </div>
              <p className="text-sm text-white/65 mb-2">{f.desc}</p>
              <div className="text-xs text-white/45">
                Avg. CPM{" "}
                <span className="cyan-text font-mono font-bold">{f.cpm}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ───────────────────────────────────────── Page
const placements = [
  {
    icon: Eye,
    title: "Above the Fold",
    desc: "Premium placement with guaranteed visibility on initial page load",
  },
  {
    icon: Pin,
    title: "Sidebar",
    desc: "Persistent placement that remains visible throughout user session",
  },
  {
    icon: FileText,
    title: "In-Content",
    desc: "Native-style integration seamlessly embedded within content flow",
  },
  {
    icon: AlignEndHorizontal,
    title: "Footer",
    desc: "Secondary engagement opportunity with targeted messaging",
  },
];

export default function Demo() {
  const [tab, setTab] = useState("Display");

  return (
    <main className="relative pt-32 pb-12">
      <div className="aurora-orb bg-[#0047FF] w-[600px] h-[600px] -top-20 -left-40 opacity-30" />
      <div className="aurora-orb bg-[#00F0FF] w-[500px] h-[500px] top-60 right-0 opacity-20" />

      {/* Hero */}
      <section className="relative pt-12 pb-12">
        <div className="container-x text-center max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass text-xs uppercase tracking-[0.2em] mb-8"
          >
            <Sparkles className="w-3.5 h-3.5 text-[#00F0FF]" />
            Live Format Showcase
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="font-display text-[clamp(2.6rem,6.5vw,5.5rem)] font-bold mb-6"
          >
            Monetisation <span className="aurora-text">Solutions</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.25 }}
            className="text-lg text-white/65 max-w-2xl mx-auto"
          >
            Explore our comprehensive suite of ad monetization products designed
            to maximize your revenue across every format and platform.
          </motion.p>

          {/* Tabs */}
          <div className="flex flex-wrap justify-center gap-2 mt-10">
            {TABS.map((t) => (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                data-testid={`demo-tab-${t.id.toLowerCase()}`}
                className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm transition-all ${
                  tab === t.id
                    ? "bg-gradient-to-r from-[#0047FF] to-[#00F0FF] text-white shadow-[0_8px_30px_rgba(0,71,255,0.4)]"
                    : "border border-white/10 text-white/65 hover:text-white hover:border-white/30"
                }`}
              >
                <t.icon className="w-4 h-4" />
                {t.id}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Tabbed panes */}
      <section className="relative py-8">
        <div className="container-x">
          <AnimatePresence mode="wait">
            <motion.div
              key={tab}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.3 }}
              data-testid={`demo-pane-${tab.toLowerCase()}`}
            >
              {tab === "Display" && <DisplayPane />}
              {tab === "oRTB" && <ORTBPane />}
              {tab === "Video" && <VideoPane />}
              {tab === "CTV" && <CTVPane />}
              {tab === "Apps" && <AppsPane />}
            </motion.div>
          </AnimatePresence>
        </div>
      </section>

      {/* Premium Placements */}
      <section className="relative py-20">
        <div className="container-x">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <div className="text-xs uppercase tracking-[0.25em] text-[#00F0FF] mb-3">
              Premium Placements
            </div>
            <h2 className="font-display text-3xl md:text-5xl font-bold">
              Strategic{" "}
              <span className="aurora-text">placement opportunities</span> for
              maximum engagement
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {placements.map((p) => (
              <div key={p.title} className="tile p-7">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#0047FF]/30 to-[#00F0FF]/20 border border-white/10 grid place-items-center mb-4">
                  <p.icon className="w-5 h-5 text-[#00F0FF]" />
                </div>
                <h3 className="font-display text-lg font-bold mb-2">
                  {p.title}
                </h3>
                <p className="text-sm text-white/60">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="relative py-20">
        <div className="container-x">
          <div className="glass-strong rounded-3xl p-10 md:p-14 grid md:grid-cols-3 gap-10 text-center">
            <div>
              <div className="font-display text-5xl md:text-6xl font-bold aurora-text">
                98%
              </div>
              <div className="font-medium mt-2">Fill Rate</div>
              <div className="text-xs text-white/50 mt-1">
                Industry-leading availability
              </div>
            </div>
            <div className="md:border-x border-white/5">
              <div className="font-display text-5xl md:text-6xl font-bold aurora-text">
                4.2x
              </div>
              <div className="font-medium mt-2">Higher CTR</div>
              <div className="text-xs text-white/50 mt-1">vs. static ads</div>
            </div>
            <div>
              <div className="font-display text-5xl md:text-6xl font-bold aurora-text">
                500+
              </div>
              <div className="font-medium mt-2">Publishers</div>
              <div className="text-xs text-white/50 mt-1">Active network</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-16">
        <div className="container-x text-center">
          <h2 className="font-display text-3xl md:text-5xl font-bold mb-4">
            Like what you see?
          </h2>
          <p className="text-white/65 mb-8 max-w-xl mx-auto">
            Get started in 24-48 hours. Our team handles integration, testing,
            and optimization end-to-end.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <a
              href="/signup"
              target="_blank"
              rel="noreferrer"
              className="btn-primary"
            >
              Start Monetizing <ArrowRight className="w-4 h-4" />
            </a>
            <a
              href="mailto:contact@monetiseup.com?subject=Schedule%20Demo"
              className="btn-ghost"
            >
              Talk to Sales
            </a>
          </div>
        </div>
      </section>
    </main>
  );
}
